import { SITE_URL } from '../config/siteUrl';
import { COMPANY } from '../config/company';
import { toCanonicalUrl } from './canonicalUrl';
import { stripHtmlForMeta } from './html';

/**
 * Helper per costruire blocchi JSON-LD (schema.org) per le pagine pubbliche.
 * Tutti i builder restituiscono oggetti JS pronti da serializzare via PublicSEO.
 *
 * Obiettivi:
 *  - dare a Google e ai crawler AI (GPTBot, ClaudeBot, PerplexityBot, ...) un grafo
 *    semantico ricco e coerente: chi siamo, cosa offriamo, chi sono i nostri medici,
 *    quali sono gli articoli del blog, le specialità mediche, le FAQ, ecc.;
 *  - centralizzare in un solo posto la conoscenza "di brand" per garantire coerenza
 *    tra pagine (stesso name/url/logo/sameAs).
 */

export const SITE_NAME = COMPANY.brandName;
export const SITE_LEGAL_NAME = COMPANY.legalNameFull;
export { SITE_URL };
export const SITE_LOGO = 'https://pamasoft.b-cdn.net/virtualclinic/images/virtual-clinic-logo-orizzontale.png';
export const SITE_DESCRIPTION = 'Piattaforma di telemedicina per visite mediche online e videoconsulti con specialisti certificati in Italia.';
export const SITE_SAME_AS = [
  'https://www.facebook.com/virtualclinic.it',
  'https://www.instagram.com/virtualclinic.it',
  'https://www.linkedin.com/company/virtual-clinic-srl/',
];

const abs = (path) => {
  if (!path) return `${SITE_URL.replace(/\/+$/, '')}/`;
  if (/^https?:\/\//i.test(path)) return path;
  return toCanonicalUrl(SITE_URL, path);
};

/** Organization core: usato come @id riferito da altri nodi (publisher, author). */
export const buildOrganization = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}#organization`,
  name: SITE_NAME,
  legalName: SITE_LEGAL_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: SITE_LOGO,
    contentUrl: SITE_LOGO,
  },
  sameAs: SITE_SAME_AS,
  contactPoint: [{
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: COMPANY.email,
    telephone: COMPANY.phones[0],
    availableLanguage: ['it'],
    areaServed: 'IT',
  }],
  taxID: COMPANY.vatNumber,
});

/** MedicalOrganization: utile in home perché Google sa che siamo un'organizzazione sanitaria. */
export const buildMedicalOrganization = () => ({
  '@context': 'https://schema.org',
  '@type': 'MedicalOrganization',
  '@id': `${SITE_URL}#medical-organization`,
  name: SITE_NAME,
  legalName: SITE_LEGAL_NAME,
  url: SITE_URL,
  logo: SITE_LOGO,
  image: SITE_LOGO,
  description: SITE_DESCRIPTION,
  sameAs: SITE_SAME_AS,
  medicalSpecialty: ['Telemedicine'],
  areaServed: { '@type': 'Country', name: 'Italia' },
  address: {
    '@type': 'PostalAddress',
    streetAddress: COMPANY.streetAddress,
    postalCode: COMPANY.postalCode,
    addressLocality: COMPANY.city,
    addressRegion: COMPANY.addressRegionCode,
    addressCountry: COMPANY.country,
  },
  contactPoint: [{
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: COMPANY.email,
    telephone: COMPANY.phones[0],
    availableLanguage: ['it'],
    areaServed: 'IT',
  }],
  taxID: COMPANY.vatNumber,
});

/** WebSite con SearchAction: abilita la sitelink-searchbox di Google. */
export const buildWebSite = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}#website`,
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESCRIPTION,
  inLanguage: 'it-IT',
  publisher: { '@id': `${SITE_URL}#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/medici?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
});

/**
 * BreadcrumbList per qualunque pagina.
 * @param {Array<{name:string, path?:string}>} items Ordinati dalla radice alla pagina corrente. L'ultimo elemento può omettere `path`.
 */
export const buildBreadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    ...(it.path ? { item: abs(it.path) } : {}),
  })),
});

/** FAQPage standard, basato su array di { q, a }. */
export const buildFAQ = (faq) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faq.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

/** Blog landing page. */
export const buildBlog = ({ path = '/blog', name = 'Argomenti', description } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${abs(path)}#blog`,
  url: abs(path),
  name,
  description: description || 'Articoli su salute, telemedicina e visite mediche online.',
  publisher: { '@id': `${SITE_URL}#organization` },
  inLanguage: 'it-IT',
});

/**
 * Articolo singolo del blog.
 *
 * Usiamo multi-type [BlogPosting, MedicalWebPage] perché:
 *  - BlogPosting → riconosciuto da Google per "Article" rich results;
 *  - MedicalWebPage → segnala a Google + LLM (AEO/GEO) che il contenuto è in ambito sanitario.
 *
 * Includiamo medicalAudience: Patient e lastReviewed (E-E-A-T YMYL), oltre a reviewedBy
 * quando l'articolo è stato revisionato da un medico identificabile (futuro: campo `reviewedBy`
 * sul payload del webhook).
 */
export const buildBlogPosting = (articolo) => {
  const url = abs(`/blog/${articolo.slug || articolo.id}`);
  const description = stripHtmlForMeta(
    articolo.metaDescription || articolo.intro || articolo.testo || '',
    300
  ) || undefined;
  const dateModified = articolo.lastUpdate || articolo.publishedAt || articolo.dataCreazione || undefined;
  return {
    '@context': 'https://schema.org',
    '@type': ['BlogPosting', 'MedicalWebPage'],
    '@id': `${url}#article`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    headline: (articolo.metaTitle || articolo.titolo || '').slice(0, 110),
    name: articolo.titolo,
    description,
    image: articolo.urlImg ? [articolo.urlImg] : undefined,
    keywords: articolo.metaKeywords || (Array.isArray(articolo.tags) ? articolo.tags.join(', ') : articolo.tags) || undefined,
    articleSection: articolo.categoria || undefined,
    inLanguage: articolo.language || 'it-IT',
    datePublished: articolo.publishedAt || articolo.dataCreazione || undefined,
    dateModified,
    lastReviewed: dateModified,
    medicalAudience: { '@type': 'MedicalAudience', audienceType: 'Patient' },
    author: {
      '@type': 'Person',
      name: 'Redazione Virtual Clinic',
      url: `${SITE_URL}/chi-siamo`,
    },
    reviewedBy: {
      '@type': 'Organization',
      name: 'Virtual Clinic',
      '@id': `${SITE_URL}#medical-organization`,
    },
    publisher: { '@id': `${SITE_URL}#organization` },
    url,
  };
};

/** Lista articoli (ItemList) per pagina blog. */
export const buildBlogItemList = (articoli) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListOrder: 'https://schema.org/ItemListOrderDescending',
  numberOfItems: articoli.length,
  itemListElement: articoli.map((a, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: abs(`/blog/${a.slug || a.id}`),
    name: a.titolo,
  })),
});

/** Physician (medico singolo). */
export const buildPhysician = (medico, opts = {}) => {
  const { canonicalPath, titolo, nomeCompleto, specialty } = opts;
  return {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    '@id': `${abs(canonicalPath)}#physician`,
    name: `${titolo || ''} ${nomeCompleto || ''}`.trim(),
    givenName: medico.nome || undefined,
    familyName: medico.cognome || undefined,
    honorificPrefix: titolo || undefined,
    medicalSpecialty: specialty || medico.branca || undefined,
    description: medico.bio || undefined,
    image: medico.urlImg || undefined,
    url: abs(canonicalPath),
    worksFor: { '@id': `${SITE_URL}#organization` },
  };
};

/** ItemList di medici per pagine listing. */
export const buildMediciItemList = (medici, mediciSlugFn) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  numberOfItems: medici.length,
  itemListElement: medici.map((m, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: abs(`/medici/${mediciSlugFn(m)}`),
    name: [m.nome, m.cognome].filter(Boolean).join(' ') || `Medico ${m.id}`,
  })),
});

/** MedicalSpecialty (pagina disciplina). */
export const buildMedicalSpecialty = (branca, opts = {}) => {
  const { canonicalPath, nome } = opts;
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalSpecialty',
    name: nome || branca.branca,
    description: branca.descrizione || branca.intro || undefined,
    url: abs(canonicalPath),
  };
};

/** ContactPage (pagina contatti). */
export const buildContactPage = ({ canonicalPath = '/contatti', name = 'Contatti' } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${abs(canonicalPath)}#contact`,
  url: abs(canonicalPath),
  name,
  isPartOf: { '@id': `${SITE_URL}#website` },
  about: { '@id': `${SITE_URL}#organization` },
});

/** WebPage generica (utile per pagine legali). */
export const buildWebPage = ({ canonicalPath, name, description, dateModified } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${abs(canonicalPath)}#webpage`,
  url: abs(canonicalPath),
  name,
  description,
  inLanguage: 'it-IT',
  isPartOf: { '@id': `${SITE_URL}#website` },
  publisher: { '@id': `${SITE_URL}#organization` },
  ...(dateModified ? { dateModified } : {}),
});

/** CollectionPage per liste (medici, discipline). */
export const buildCollectionPage = ({ canonicalPath, name, description } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${abs(canonicalPath)}#collection`,
  url: abs(canonicalPath),
  name,
  description,
  inLanguage: 'it-IT',
  isPartOf: { '@id': `${SITE_URL}#website` },
});
